/**
 * global.js — Buyza Premium Theme
 * Utilities: scroll-reveal, drawer, overlay, quantity, search
 */

/* ─── SCROLL REVEAL ──────────────────────────────────────── */
(function initReveal() {
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' }
  );

  document.querySelectorAll('.reveal, .reveal-children').forEach((el) => {
    observer.observe(el);
  });

  // Re-run for dynamically added elements
  window._revealObserver = observer;
})();

/* ─── OVERLAY ─────────────────────────────────────────────── */
const Overlay = {
  el: null,
  init() {
    this.el = document.getElementById('site-overlay');
    if (!this.el) return;
    this.el.addEventListener('click', () => Drawer.closeAll());
  },
  open()  { this.el && this.el.classList.add('is-open'); document.body.style.overflow = 'hidden'; },
  close() { this.el && this.el.classList.remove('is-open'); document.body.style.overflow = ''; },
};

/* ─── DRAWER ──────────────────────────────────────────────── */
const Drawer = {
  openDrawer: null,

  open(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    if (this.openDrawer) this.close(this.openDrawer.id);
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    this.openDrawer = drawer;
    Overlay.open();
    // Focus first focusable element
    const first = drawer.querySelector('button, [href], input');
    if (first) first.focus();
  },

  close(id) {
    const drawer = id ? document.getElementById(id) : this.openDrawer;
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    this.openDrawer = null;
    Overlay.close();
  },

  closeAll() {
    this.close();
  },
};

/* ─── WISHLIST ────────────────────────────────────────────── */
const Wishlist = {
  KEY: 'buyza:wishlist',

  getAll() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY)) || [];
    } catch (e) {
      return [];
    }
  },

  save(items) {
    try {
      localStorage.setItem(this.KEY, JSON.stringify(items));
    } catch (e) { /* storage unavailable — fail silently */ }
    this.render();
  },

  has(handle) {
    return this.getAll().some((item) => item.handle === handle);
  },

  add(item) {
    const items = this.getAll();
    if (!items.some((i) => i.handle === item.handle)) {
      items.push(item);
      this.save(items);
    }
  },

  remove(handle) {
    this.save(this.getAll().filter((i) => i.handle !== handle));
  },

  toggle(item) {
    if (!item.handle) return;
    if (this.has(item.handle)) {
      this.remove(item.handle);
    } else {
      this.add(item);
    }
  },

  syncButtons() {
    const items = this.getAll();
    document.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      btn.classList.toggle('is-active', items.some((i) => i.handle === btn.dataset.handle));
    });
  },

  renderDrawerList() {
    const list  = document.getElementById('wishlist-drawer-list');
    const empty = document.getElementById('wishlist-drawer-empty');
    const count = document.getElementById('wishlist-count');
    const items = this.getAll();

    if (count) {
      count.textContent = items.length;
      count.hidden = items.length === 0;
    }
    if (!list) return;
    if (empty) empty.hidden = items.length > 0;
    list.hidden = items.length === 0;

    list.textContent = '';
    items.forEach((item) => {
      const li = document.createElement('li');
      li.className = 'wishlist-item';

      const mediaLink = document.createElement('a');
      mediaLink.href = item.url || '#';
      mediaLink.className = 'wishlist-item__media';
      const img = document.createElement('img');
      img.src = item.image || '';
      img.alt = item.title || '';
      img.width = 64;
      img.height = 64;
      img.loading = 'lazy';
      mediaLink.appendChild(img);

      const body = document.createElement('div');
      body.className = 'wishlist-item__body';
      const titleLink = document.createElement('a');
      titleLink.href = item.url || '#';
      titleLink.className = 'wishlist-item__title';
      titleLink.textContent = item.title || '';
      const price = document.createElement('span');
      price.className = 'wishlist-item__price';
      price.textContent = item.price || '';
      body.append(titleLink, price);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'wishlist-item__remove';
      removeBtn.setAttribute('aria-label', 'Remove from wishlist');
      removeBtn.dataset.wishlistRemove = item.handle;
      removeBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6"/></svg>';

      li.append(mediaLink, body, removeBtn);
      list.appendChild(li);
    });
  },

  render() {
    this.syncButtons();
    this.renderDrawerList();
  },

  init() {
    this.render();

    document.addEventListener('click', (e) => {
      const toggleBtn = e.target.closest('[data-wishlist-toggle]');
      if (toggleBtn) {
        this.toggle({
          handle: toggleBtn.dataset.handle,
          title:  toggleBtn.dataset.title,
          image:  toggleBtn.dataset.image,
          price:  toggleBtn.dataset.price,
          url:    toggleBtn.dataset.url,
        });
        return;
      }

      const removeBtn = e.target.closest('[data-wishlist-remove]');
      if (removeBtn) {
        this.remove(removeBtn.dataset.wishlistRemove);
      }
    });
  },
};

/* ─── QUANTITY SELECTOR ───────────────────────────────────── */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('.qty__btn');
  if (!btn) return;
  const input = btn.closest('.qty').querySelector('.qty__input');
  if (!input) return;
  const step  = btn.dataset.action === 'minus' ? -1 : 1;
  const min   = parseInt(input.min) || 1;
  const max   = parseInt(input.max) || 999;
  const value = Math.min(max, Math.max(min, parseInt(input.value || 1) + step));
  input.value = value;
  input.dispatchEvent(new Event('change', { bubbles: true }));
});

/* ─── MOBILE MENU ─────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  Overlay.init();
  Wishlist.init();

  // Toggle buttons wired via data-drawer-open="<id>"
  document.querySelectorAll('[data-drawer-open]').forEach((btn) => {
    btn.addEventListener('click', () => Drawer.open(btn.dataset.drawerOpen));
  });

  document.querySelectorAll('[data-drawer-close]').forEach((btn) => {
    btn.addEventListener('click', () => Drawer.closeAll());
  });

  // ESC to close
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') Drawer.closeAll();
  });

  // Announcement bar close
  const announcementClose = document.getElementById('announcement-close');
  const announcementBar   = document.getElementById('announcement-bar');
  if (announcementClose && announcementBar) {
    announcementClose.addEventListener('click', () => {
      announcementBar.style.height = announcementBar.offsetHeight + 'px';
      requestAnimationFrame(() => {
        announcementBar.style.transition = 'height 0.3s ease, opacity 0.3s ease, min-height 0.3s ease';
        announcementBar.style.height     = '0';
        announcementBar.style.minHeight  = '0';
        announcementBar.style.opacity    = '0';
        announcementBar.style.overflow   = 'hidden';
      });
    });

    // Some browsers restore the closed inline styles from bfcache on reload/back
    // navigation instead of a fresh load — force the bar back to its default state.
    window.addEventListener('pageshow', () => {
      announcementBar.style.transition = 'none';
      announcementBar.style.height     = '';
      announcementBar.style.minHeight  = '';
      announcementBar.style.opacity    = '';
      announcementBar.style.overflow   = '';
    });
  }

  // Header scroll shadow
  const header = document.getElementById('site-header');
  if (header) {
    const onScroll = () => {
      header.classList.toggle('is-scrolled', window.scrollY > 10);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  // Re-observe any late-rendered reveal elements
  document.querySelectorAll('.reveal:not(.is-observed), .reveal-children:not(.is-observed)').forEach((el) => {
    el.classList.add('is-observed');
    window._revealObserver && window._revealObserver.observe(el);
  });
});

/* ─── EXPOSE GLOBALS ──────────────────────────────────────── */
window.BuyzaDrawer    = Drawer;
window.BuyzaOverlay   = Overlay;
window.BuyzaWishlist  = Wishlist;
